import { useQuery } from "@tanstack/react-query";
import { Link, useLocation, useParams } from "react-router-dom";
import api from "@/lib/api";
import { LayoutGrid, Plus, X, Star, HelpCircle, ShieldCheck, Users, Building2, MessageSquare, Calendar as CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { dotFor } from "@/lib/boardColors";
import CreateWorkspaceDialog from "@/components/dialogs/CreateWorkspaceDialog";
import WorkspaceKebab, { WorkspaceContextMenu } from "@/components/WorkspaceKebab";
import { useEffect, useState } from "react";
import { useAuth } from "@/components/AuthProvider";
import { useTranslation } from "react-i18next";

export default function Sidebar({ open, onClose }) {
  const location = useLocation();
  const params = useParams();
  const [createOpen, setCreateOpen] = useState(false);
  const { user } = useAuth();
  const { t } = useTranslation();

  // Auto-close the mobile drawer whenever the route changes.
  useEffect(() => {
    if (open) onClose?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  const { data: workspaces = [] } = useQuery({
    queryKey: ["workspaces"],
    queryFn: async () => (await api.get("/workspaces")).data,
  });

  const { data: recentBoards = [] } = useQuery({
    queryKey: ["recent-boards"],
    queryFn: async () => (await api.get("/boards/recent", { params: { limit: 8 } })).data,
  });

  const { data: sharedBoards = [] } = useQuery({
    queryKey: ["shared-boards"],
    queryFn: async () => (await api.get("/boards/shared")).data,
  });

  const { data: myOrg } = useQuery({
    queryKey: ["org-me-sidebar"],
    queryFn: async () => {
      try { return (await api.get("/org/me")).data; }
      catch { return null; }
    },
    retry: false,
    staleTime: 60_000,
  });

  const { data: chatThreads } = useQuery({
    queryKey: ["chat-threads-sidebar"],
    queryFn: async () => {
      try { return (await api.get("/chat/threads")).data; }
      catch { return null; }
    },
    enabled: !!myOrg?.org,
    retry: false,
    refetchInterval: 20_000,
  });
  const totalUnread = chatThreads
    ? (chatThreads.broadcast?.unread || 0) + (chatThreads.direct || []).reduce((s, t) => s + (t.unread || 0), 0)
    : 0;

  // Boards that *I* own (or co-own) — exclude shared_with_me.
  const ownedRecent = recentBoards.filter(b => b.relation !== "shared_with_me");

  return (
    <>
      {/* Mobile overlay */}
      <div
        onClick={onClose}
        className={cn(
          "fixed inset-0 bg-black/30 z-30 md:hidden transition-opacity",
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
      />
      <aside
        data-testid="sidebar"
        className={cn(
          "w-64 border-r border-white/40 dark:border-white/10 bg-white/30 dark:bg-slate-900/30 backdrop-blur-xl flex flex-col z-40 transition-transform",
          "md:translate-x-0 md:relative md:flex md:h-full md:top-auto md:bottom-auto",
          open
            ? "fixed left-0 top-16 bottom-0 translate-x-0"
            : "fixed left-0 top-16 bottom-0 -translate-x-full md:translate-x-0"
        )}
      >
        <div className="p-4 flex items-center justify-between md:hidden">
          <span className="font-display font-semibold">{t("sidebar.menu")}</span>
          <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-white/40 dark:hover:bg-white/10"><X className="h-4 w-4" /></button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto kanban-scroll">

        <div className="p-4 space-y-1">
          <Link
            to="/"
            data-testid="sidebar-home"
            className={cn(
              "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition",
              location.pathname === "/"
                ? "bg-blue-500/10 text-blue-600 dark:text-blue-300"
                : "hover:bg-white/40 dark:hover:bg-white/10"
            )}
          >
            <LayoutGrid className="h-4 w-4" /> {t("sidebar.dashboard")}
          </Link>
          <Link
            to="/calendar"
            data-testid="sidebar-calendar"
            className={cn(
              "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition",
              location.pathname.startsWith("/calendar")
                ? "bg-blue-500/10 text-blue-600 dark:text-blue-300"
                : "hover:bg-white/40 dark:hover:bg-white/10"
            )}
          >
            <CalendarIcon className="h-4 w-4" /> {t("sidebar.calendar", "Calendario")}
          </Link>
          <Link
            to="/help"
            data-testid="sidebar-help"
            className={cn(
              "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition",
              location.pathname === "/help"
                ? "bg-blue-500/10 text-blue-600 dark:text-blue-300"
                : "hover:bg-white/40 dark:hover:bg-white/10"
            )}
          >
            <HelpCircle className="h-4 w-4" /> {t("sidebar.help")}
          </Link>
          {user?.is_admin && (
            <Link
              to="/admin"
              data-testid="sidebar-admin"
              className={cn(
                "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition",
                location.pathname.startsWith("/admin")
                  ? "bg-red-500/10 text-red-600 dark:text-red-300"
                  : "hover:bg-white/40 dark:hover:bg-white/10"
              )}
            >
              <ShieldCheck className="h-4 w-4" /> {t("sidebar.superAdmin")}
            </Link>
          )}
          {myOrg?.org && (
            <Link
              to="/org"
              data-testid="sidebar-org"
              className={cn(
                "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition",
                location.pathname.startsWith("/org")
                  ? "bg-blue-500/10 text-blue-600 dark:text-blue-300"
                  : "hover:bg-white/40 dark:hover:bg-white/10"
              )}
            >
              <Building2 className="h-4 w-4" /> {myOrg.org.name}
            </Link>
          )}
          {myOrg?.org && (
            <Link
              to="/chat"
              data-testid="sidebar-chat"
              className={cn(
                "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition",
                location.pathname.startsWith("/chat")
                  ? "bg-blue-500/10 text-blue-600 dark:text-blue-300"
                  : "hover:bg-white/40 dark:hover:bg-white/10"
              )}
            >
              <MessageSquare className="h-4 w-4" /> Chat del equipo
              {totalUnread > 0 && (
                <span
                  data-testid="sidebar-chat-unread-badge"
                  className="ml-auto bg-blue-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1.5 grid place-items-center"
                >
                  {totalUnread > 99 ? "99+" : totalUnread}
                </span>
              )}
            </Link>
          )}
        </div>

        <div className="px-4 mt-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("sidebar.workspaces")}</span>
            <button
              data-testid="sidebar-create-workspace"
              onClick={() => setCreateOpen(true)}
              className="h-6 w-6 grid place-items-center rounded-md hover:bg-white/40 dark:hover:bg-white/10"
              aria-label={t("sidebar.createWorkspace")}
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="space-y-1">
            {workspaces.map((w) => (
              <div key={w.workspace_id} className="group relative">
                <WorkspaceContextMenu workspace={w}>
                  <Link
                    to={`/workspaces/${w.workspace_id}`}
                    data-testid={`sidebar-workspace-${w.workspace_id}`}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 pr-9 rounded-lg text-sm transition",
                      params.workspaceId === w.workspace_id
                        ? "bg-blue-500/10 text-blue-600 dark:text-blue-300"
                        : "hover:bg-white/40 dark:hover:bg-white/10"
                    )}
                  >
                    <span className={cn("h-2 w-2 rounded-full", dotFor(w.color))} />
                    <span className="truncate">{w.name}</span>
                  </Link>
                </WorkspaceContextMenu>
                <div className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                  <WorkspaceKebab workspace={w} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="px-4 mt-6">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("sidebar.myBoards")}</span>
          <div className="mt-2 space-y-1">
            {ownedRecent.map((b) => (
              <Link
                key={b.board_id}
                to={`/boards/${b.board_id}`}
                data-testid={`sidebar-board-${b.board_id}`}
                className={cn(
                  "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition truncate",
                  params.boardId === b.board_id
                    ? "bg-blue-500/10 text-blue-600 dark:text-blue-300"
                    : "hover:bg-white/40 dark:hover:bg-white/10"
                )}
              >
                <span className={cn("h-2 w-2 rounded-full flex-none", dotFor(b.cover_color))} />
                <span className="truncate flex-1">{b.name}</span>
                {b.is_starred && <Star className="h-3 w-3 text-amber-400 fill-amber-400" />}
              </Link>
            ))}
            {ownedRecent.length === 0 && (
              <p className="text-xs text-muted-foreground px-3 py-2">{t("sidebar.noBoards")}</p>
            )}
          </div>
        </div>

        {sharedBoards.length > 0 && (
          <div className="px-4 mt-6" data-testid="sidebar-shared-section">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
              <Users className="h-3 w-3" /> {t("sidebar.sharedWithYou")}
              <span className="text-muted-foreground/70 normal-case ml-1">({sharedBoards.length})</span>
            </span>
            <div className="mt-2 space-y-1">
              {sharedBoards.map((b) => (
                <Link
                  key={b.board_id}
                  to={`/boards/${b.board_id}`}
                  data-testid={`sidebar-shared-board-${b.board_id}`}
                  className={cn(
                    "group flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition",
                    params.boardId === b.board_id
                      ? "bg-blue-500/10 text-blue-600 dark:text-blue-300"
                      : "hover:bg-white/40 dark:hover:bg-white/10"
                  )}
                  title={b.members?.map(m => m.name || m.email).join(", ")}
                >
                  <span className={cn("h-2 w-2 rounded-full flex-none", dotFor(b.cover_color))} />
                  <span className="truncate flex-1">{b.name}</span>
                  <MemberAvatars members={b.members} />
                </Link>
              ))}
            </div>
          </div>
        )}
        </div>{/* /scroll */}

        <div className="mt-auto p-4 border-t border-white/40 dark:border-white/10 flex-none">
          <a
            href="https://mentedigital.com"
            target="_blank"
            rel="noreferrer noopener"
            data-testid="sidebar-credit"
            className="flex flex-col items-center gap-1.5 px-2 py-2 rounded-lg hover:bg-white/40 dark:hover:bg-white/10 transition group"
          >
            <span className="text-[11px] text-muted-foreground group-hover:text-foreground transition leading-tight">
              {t("sidebar.createdBy")}
            </span>
            <span className="w-full inline-flex items-center justify-center h-10 px-3 rounded-md bg-slate-900 dark:bg-white/5">
              <img
                src="https://mentedigital.com/imagenes/logo_Mente_Digital_blanco.png"
                alt="Mente Digital"
                className="h-[19px] w-auto object-contain opacity-90 group-hover:opacity-100 transition"
                loading="lazy"
              />
            </span>
          </a>
        </div>
      </aside>

      <CreateWorkspaceDialog open={createOpen} onOpenChange={setCreateOpen} />
    </>
  );
}

function MemberAvatars({ members = [] }) {
  const visible = members.slice(0, 3);
  const extra = members.length - visible.length;
  return (
    <div className="flex -space-x-1.5 flex-none">
      {visible.map(m => {
        const label = m.name || m.email || "?";
        const initial = label[0]?.toUpperCase() || "?";
        return m.picture ? (
          <img
            key={m.user_id}
            src={m.picture}
            alt={label}
            title={label}
            className="h-5 w-5 rounded-full ring-2 ring-white dark:ring-slate-900 object-cover"
          />
        ) : (
          <span
            key={m.user_id}
            title={label}
            className="h-5 w-5 rounded-full ring-2 ring-white dark:ring-slate-900 bg-blue-500 text-white text-[9px] font-bold grid place-items-center"
          >
            {initial}
          </span>
        );
      })}
      {extra > 0 && (
        <span className="h-5 w-5 rounded-full ring-2 ring-white dark:ring-slate-900 bg-slate-600 text-white text-[9px] font-bold grid place-items-center">
          +{extra}
        </span>
      )}
    </div>
  );
}
